import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Line, LineChart, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend } from 'recharts';
import { PerformanceSummary } from '@/hooks/usePartnerAnalytics';
import { AlertTriangle } from 'lucide-react';

interface RiskTrendChartProps {
  contractors: PerformanceSummary[];
}

export function RiskTrendChart({ contractors }: RiskTrendChartProps) {
  // Calculate risk score based on multiple factors (simulating get_contractor_risk_score)
  const calculateRiskScore = (contractor: PerformanceSummary): number => {
    let risk = 0;
    
    // Poor rating increases risk
    if (contractor.overall_rating && contractor.overall_rating < 3) {
      risk += 30;
    } else if (contractor.overall_rating && contractor.overall_rating < 4) {
      risk += 15;
    }
    
    // Pending documents increase risk
    risk += contractor.pending_documents * 5;
    
    // Rejected documents significantly increase risk
    risk += contractor.rejected_documents * 15;
    
    // Expiring status increases risk
    if (contractor.prequalification_status === 'expiring_soon') risk += 20;
    if (contractor.prequalification_status === 'expired') risk += 40;
    
    return Math.min(100, risk);
  };

  // Get high-risk contractors
  const contractorsWithRisk = contractors
    .map(c => ({ ...c, riskScore: calculateRiskScore(c) }))
    .filter(c => c.riskScore > 30)
    .sort((a, b) => b.riskScore - a.riskScore)
    .slice(0, 5);

  // Generate simulated trend data (3 months)
  const months = ['3 months ago', '2 months ago', 'Last month', 'Current'];
  const trendData = months.map((month, index) => {
    const dataPoint: any = { month };
    contractorsWithRisk.forEach((contractor, cIndex) => {
      // Simulate increasing trend
      const variance = (Math.random() - 0.5) * 10;
      dataPoint[`contractor${cIndex}`] = Math.max(0, contractor.riskScore - (3 - index) * 5 + variance);
    });
    return dataPoint;
  });

  const colors = ['hsl(var(--destructive))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'];

  const chartConfig = contractorsWithRisk.reduce((acc, contractor, index) => ({
    ...acc,
    [`contractor${index}`]: {
      label: contractor.name,
      color: colors[index],
    },
  }), {});

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          Risk Trend Analysis
        </CardTitle>
      </CardHeader>
      <CardContent>
        {contractorsWithRisk.length > 0 ? (
          <>
            <div className="mb-4 p-3 bg-destructive/10 rounded-lg">
              <p className="text-sm text-destructive font-medium">
                {contractorsWithRisk.length} contractor(s) with elevated risk levels detected
              </p>
            </div>

            <ChartContainer config={chartConfig} className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fill: 'hsl(var(--foreground))', fontSize: 11 }}
                  />
                  <YAxis 
                    domain={[0, 100]}
                    label={{ value: 'Risk Score', angle: -90, position: 'insideLeft' }}
                    tick={{ fill: 'hsl(var(--foreground))' }}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Legend />
                  {contractorsWithRisk.map((contractor, index) => (
                    <Line
                      key={contractor.id}
                      type="monotone"
                      dataKey={`contractor${index}`}
                      stroke={colors[index]}
                      name={contractor.name}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>

            <div className="mt-4 space-y-2">
              <p className="text-sm font-medium">Risk Factors:</p>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                <li>Low ratings (&lt;3 stars): High risk</li>
                <li>Pending documents: +5 per document</li>
                <li>Rejected documents: +15 per document</li>
                <li>Expiring/Expired status: +20-40 points</li>
              </ul>
            </div>
          </>
        ) : (
          <div className="h-[350px] flex items-center justify-center text-muted-foreground">
            No high-risk contractors detected
          </div>
        )}
      </CardContent>
    </Card>
  );
}
